import { useEffect, useMemo, useRef, useState, type MutableRefObject } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  campaignsDataAtom,
  googleAdsAccountAtom,
  organizationDataAtom,
  strategyKpiTargetsAtom,
} from '@/store';
import {
  accountKpiTargetsQueryKey,
  accountKpiTargetsQueryOptions,
  strategyKpiPerformanceQueryOptions,
} from '@/lib/strategy/query-options';
import {
  type AccountKpiTargetsUpsert,
  type AccountKpiTargetsWire,
} from '@/lib/strategy/api';
import { cn } from '@/lib/utils';

type PromotedKpi = 'cpa' | 'conversions' | 'roas';

/**
 * Save controller the parent's atomic "Save Changes" (#5401) uses instead of a self-contained save:
 * it pulls the KPI payload to fold into the one request, then hands back the saved row to reset the
 * card's baseline.
 */
export interface KpiTargetsSaveControl {
  /** KPI upsert body to include in the atomic save, or null when no Ads account is connected. */
  getPayload: () => AccountKpiTargetsUpsert | null;
  /** Adopt the server's saved targets as the new baseline (clears dirty). */
  markSaved: (saved: AccountKpiTargetsWire) => void;
}

// 'budget'/spend was removed as a user-facing KPI. Legacy accounts may still
// have it persisted as the focus KPI; treat that as CPA.
function normalizeFocus(f: string | null | undefined): PromotedKpi {
  if (f === 'cpa' || f === 'conversions' || f === 'roas') return f;
  return 'cpa';
}

interface KpiTargets {
  cpa: number;
  roas: number;
  budget: number;
  conversions: number;
  aov: number;
}

function round2(value: number) {
  return Math.round(value * 100) / 100;
}

// ROAS is rendered as the gross multiplier (1.5 = 1.5x return); the
// advisor/server wire format is the net return percent (50 = "+50%").
// Conversion happens at the API boundary in this file.
function roasPctToMultiplier(pct: number): number {
  return pct / 100 + 1;
}

function roasMultiplierToPct(multiplier: number): number {
  return (multiplier - 1) * 100;
}

function formatKpiValue(key: 'cpa' | 'roas' | 'budget' | 'conversions' | 'aov', value: number) {
  if (key === 'roas') {
    return `${value.toFixed(2)}x`;
  }
  const rounded = round2(value);
  const formatted = rounded.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  if (key === 'cpa' || key === 'budget' || key === 'aov') return `€${formatted}`;
  return formatted;
}

const FOCUS_CHIPS: { key: PromotedKpi; label: string }[] = [
  { key: 'cpa', label: 'CPA' },
  { key: 'conversions', label: 'Conversions' },
  { key: 'roas', label: 'ROAS' },
];

const FOCUS_FIELD_META: Record<
  PromotedKpi,
  { label: string; prefix?: string; suffix?: string; step: number; allowZero: boolean }
> = {
  cpa: { label: 'CPA goal', prefix: '€', step: 1, allowZero: false },
  conversions: { label: 'Conversions goal', step: 1, allowZero: false },
  roas: { label: 'ROAS goal', step: 0.1, allowZero: true },
};

// One standard size for every numeric field on the card — sized to the
// values (short currency/count/ratio numbers), not stretched to the column.
const FIELD_WIDTH = 'w-44';
const FIELD_INPUT_CLASS = 'h-9 bg-white text-sm font-medium';

export function YourGoalKpiCard({
  onDirtyChange,
  saveRef,
}: {
  /** Reports whether the KPI targets diverge from what's persisted, so the
   * parent's "Save Changes" button lights up. */
  onDirtyChange?: (dirty: boolean) => void;
  /** The parent registers this controller and folds the KPI payload into its atomic save — KPI
   * targets save with the rest of the business section, not on their own (#5401). */
  saveRef?: MutableRefObject<KpiTargetsSaveControl | null>;
} = {}) {
  const organizationData = useAtomValue(organizationDataAtom);
  // The active org id is threaded explicitly into the strategy queries and
  // mutations below (#4999) — no module-level side channel.
  const organisationId = organizationData?.id ?? null;

  const [kpiTargets, setKpiTargets] = useAtom(strategyKpiTargetsAtom);
  const googleAdsAccount = useAtomValue(googleAdsAccountAtom);
  const campaigns = useAtomValue(campaignsDataAtom);
  const accountCampaignIds = useMemo(
    () => campaigns.map((c) => c.id).join(','),
    [campaigns],
  );
  const queryClient = useQueryClient();

  const googleAdsAccountId = useMemo<number | null>(() => {
    if (!googleAdsAccount) return null;
    const parsed = Number(googleAdsAccount.id);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [googleAdsAccount]);

  // Pulls 90d performance across every SEARCH campaign on the connected Ads
  // account, not just Glitch-managed ones. Fresh accounts (no Glitch
  // campaigns yet) still get a real baseline for goal-setting.
  const { data: perfData } = useQuery({
    ...strategyKpiPerformanceQueryOptions(organisationId, accountCampaignIds),
    enabled: organisationId !== null && !!googleAdsAccount && accountCampaignIds.length > 0,
  });

  const hasPerfData = (perfData?.length ?? 0) > 0;

  const trackedConversionValue = useMemo(
    () => (perfData ?? []).reduce((sum, p) => sum + (p.all_conversions_value ?? 0), 0),
    [perfData],
  );
  const trackedConversions = useMemo(
    () => (perfData ?? []).reduce((sum, p) => sum + (p.conversions ?? 0), 0),
    [perfData],
  );
  const hasTrackedConversionValue = trackedConversionValue > 0;
  const trackedAov = hasTrackedConversionValue && trackedConversions > 0
    ? trackedConversionValue / trackedConversions
    : 0;

  // KPI targets load as soon as we have an Ads account — no longer gated on
  // perf data, so the form renders even for accounts with zero SEARCH spend
  // in the last 90 days.
  const { data: kpiTargetsServer } = useQuery({
    ...accountKpiTargetsQueryOptions(organisationId, googleAdsAccountId),
    enabled: organisationId !== null && googleAdsAccountId !== null && hasPerfData,
  });
  const hydratedAccountIdRef = useRef<number | null>(null);
  useEffect(() => {
    if (kpiTargetsServer === undefined) return;
    if (googleAdsAccountId === null) return;
    if (hydratedAccountIdRef.current === googleAdsAccountId) return;
    const allZero =
      kpiTargetsServer.cpa === 0 &&
      kpiTargetsServer.roas === 0 &&
      kpiTargetsServer.spend === 0 &&
      kpiTargetsServer.conversions === 0 &&
      kpiTargetsServer.aov === 0;
    if (!allZero) {
      setKpiTargets({
        cpa: kpiTargetsServer.cpa,
        roas: roasPctToMultiplier(kpiTargetsServer.roas),
        budget: 0,
        conversions: kpiTargetsServer.conversions,
        aov: kpiTargetsServer.aov,
        focusKpi: normalizeFocus(kpiTargetsServer.focus_kpi),
      });
    } else if (kpiTargetsServer.focus_kpi) {
      setKpiTargets((prev) => ({ ...prev, focusKpi: normalizeFocus(kpiTargetsServer.focus_kpi) }));
    }
    hydratedAccountIdRef.current = googleAdsAccountId;
  }, [kpiTargetsServer, googleAdsAccountId, setKpiTargets]);

  useEffect(() => {
    if (
      hydratedAccountIdRef.current !== null &&
      hydratedAccountIdRef.current !== googleAdsAccountId
    ) {
      hydratedAccountIdRef.current = null;
    }
  }, [googleAdsAccountId]);

  // The user's manual entry always wins; the Google-tracked value is only a
  // prefill default when they haven't overridden it.
  const effectiveAov = kpiTargets.aov > 0 ? kpiTargets.aov : trackedAov;

  const isDirty =
    kpiTargetsServer === undefined
      ? false
      : kpiTargets.cpa !== kpiTargetsServer.cpa ||
        Math.abs(kpiTargets.roas - roasPctToMultiplier(kpiTargetsServer.roas)) > 1e-4 ||
        kpiTargets.conversions !== kpiTargetsServer.conversions ||
        effectiveAov !== kpiTargetsServer.aov ||
        kpiTargets.focusKpi !== normalizeFocus(kpiTargetsServer.focus_kpi);

  const promotedKpi: PromotedKpi = normalizeFocus(kpiTargets.focusKpi);

  // Returns the goal value in the wire format the server expects — that
  // is: ROAS converted from the UI multiplier back to a net percent.
  const goalValueForFocus = (kpi: PromotedKpi): number => {
    if (kpi === 'cpa') return kpiTargets.cpa;
    if (kpi === 'conversions') return kpiTargets.conversions;
    return roasMultiplierToPct(kpiTargets.roas);
  };

  // KPI targets save through the business section's shared "Save Changes" button, folded into the
  // parent's atomic request (#5401). Expose the payload to include + a baseline reset the parent
  // calls with the server's saved row. No deps array so the controller always closes over the
  // latest edits.
  useEffect(() => {
    if (!saveRef) return;
    saveRef.current = {
      getPayload: () =>
        googleAdsAccountId === null
          ? null
          : {
              googleadsaccount_id: googleAdsAccountId,
              cpa: kpiTargets.cpa,
              roas: roasMultiplierToPct(kpiTargets.roas),
              spend: 0,
              conversions: kpiTargets.conversions,
              aov: effectiveAov,
              focus_kpi: kpiTargets.focusKpi,
              goal_value: goalValueForFocus(normalizeFocus(kpiTargets.focusKpi)),
            },
      markSaved: (saved) => {
        queryClient.setQueryData<AccountKpiTargetsWire>(
          accountKpiTargetsQueryKey(saved.organisation_id, saved.googleadsaccount_id),
          saved,
        );
      },
    };
    return () => {
      if (saveRef) saveRef.current = null;
    };
  });
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const handlePromoteKpi = (kpi: PromotedKpi) => {
    setKpiTargets({ ...kpiTargets, focusKpi: kpi });
  };
  const handleKpiChange = (patch: Partial<KpiTargets>) => {
    setKpiTargets({ ...kpiTargets, ...patch });
  };

  // 90-day historical averages, computed from the daily performance series
  // (`perfData` is 90 days of daily rows). Spend and conversions are scaled
  // to a 30-day monthly figure so they're comparable with the goal inputs,
  // which the rest of the platform treats as monthly. CPA and ROAS are
  // window aggregates (total cost / total conversions, total value / total
  // cost), independent of window length. ROAS is shown as a multiplier to
  // match the input format.
  const historicalAvg = useMemo<{
    cpa: number;
    conversions: number;
    roas: number;
  } | null>(() => {
    const rows = perfData ?? [];
    if (rows.length === 0) return null;
    const totalCost = rows.reduce((s, r) => s + (r.cost ?? 0), 0);
    const totalConv = rows.reduce((s, r) => s + (r.conversions ?? 0), 0);
    const totalValue = rows.reduce((s, r) => s + (r.all_conversions_value ?? 0), 0);
    const monthFactor = 30 / rows.length;
    return {
      conversions: totalConv * monthFactor,
      cpa: totalConv > 0 ? totalCost / totalConv : 0,
      roas: totalCost > 0 ? totalValue / totalCost : 0,
    };
  }, [perfData]);


  if (googleAdsAccountId === null) {
    return (
      <div className='space-y-1'>
        <h2 className='text-lg font-semibold text-gray-900'>KPI Targets</h2>
        <p className='text-sm text-gray-500'>
          Select a Google Ads account to set KPI targets.
        </p>
      </div>
    );
  }

  // Wait for the saved targets to come back so the inputs don't flash zeroed
  // defaults or autosave an uninitialised state.
  if (kpiTargetsServer === undefined) {
    return (
      <div className='space-y-3'>
        <div>
          <h2 className='text-lg font-semibold text-gray-900'>KPI Targets</h2>
          <p className='text-sm text-gray-500 mt-0.5'>Loading saved targets…</p>
        </div>
        <div className='h-24 rounded-md border border-gray-200 bg-gray-50 animate-pulse' />
      </div>
    );
  }

  // The form renders regardless of whether the account has 90-day SEARCH
  // history yet — the historical-average column just collapses to em-dashes
  // and the AOV input falls back to manual entry.
  const contextCopy =
    perfData === undefined
      ? 'Set your KPI targets. The last 90 days of account performance is being loaded for context.'
      : historicalAvg === null
        ? 'Set your KPI targets. No SEARCH activity recorded in the last 90 days yet; historical context will appear once data comes in.'
        : 'Set your KPI targets. The last 90 days of account performance is shown for context.';

  return (
    <div className='space-y-5'>
      <div>
        <h2 className='text-lg font-semibold text-gray-900'>KPI Targets</h2>
        <p className='text-sm text-gray-500 mt-0.5'>{contextCopy}</p>
      </div>
      <FocusKpiSelector promoted={promotedKpi} onPromote={handlePromoteKpi} />
      <AllKpiInputs
        targets={kpiTargets}
        onChange={handleKpiChange}
        historicalAvg={historicalAvg}
      />
      <AovInput
        value={kpiTargets.aov}
        onChange={(v) => handleKpiChange({ aov: v })}
        trackedAov={trackedAov}
      />
    </div>
  );
}

function FocusKpiSelector({
  promoted,
  onPromote,
}: {
  promoted: PromotedKpi;
  onPromote: (next: PromotedKpi) => void;
}) {
  return (
    <div className='space-y-2'>
      <p className='text-xs uppercase tracking-wide text-gray-500 font-semibold'>
        Primary KPI
      </p>
      <RadioGroup
        value={promoted}
        onValueChange={(v) => onPromote(v as PromotedKpi)}
        className='flex flex-wrap items-center gap-x-6 gap-y-2'
      >
        {FOCUS_CHIPS.map(({ key, label }) => (
          <div key={key} className='flex items-center gap-2'>
            <RadioGroupItem value={key} id={`primary-kpi-${key}`} />
            <Label
              htmlFor={`primary-kpi-${key}`}
              className='text-sm font-medium text-gray-900 cursor-pointer'
            >
              {label}
            </Label>
          </div>
        ))}
      </RadioGroup>
    </div>
  );
}

const KPI_KEYS: PromotedKpi[] = ['cpa', 'conversions', 'roas'];

function AllKpiInputs({
  targets,
  onChange,
  historicalAvg,
}: {
  targets: KpiTargets;
  onChange: (patch: Partial<KpiTargets>) => void;
  historicalAvg: { cpa: number; conversions: number; roas: number } | null;
}) {
  return (
    <div className='space-y-2'>
      <div className='flex items-baseline justify-between'>
        <p className='text-xs uppercase tracking-wide text-gray-500 font-semibold'>
          KPI targets
        </p>
        <p className='text-[11px] text-gray-500'>90-day historical average</p>
      </div>
      <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
        {KPI_KEYS.map((key) => (
          <KpiInputBlock
            key={key}
            kpi={key}
            targets={targets}
            onChange={onChange}
            historicalValue={historicalAvg ? historicalAvg[key] : null}
          />
        ))}
      </div>
    </div>
  );
}

function KpiInputBlock({
  kpi,
  targets,
  onChange,
  historicalValue,
}: {
  kpi: PromotedKpi;
  targets: KpiTargets;
  onChange: (patch: Partial<KpiTargets>) => void;
  historicalValue: number | null;
}) {
  const meta = FOCUS_FIELD_META[kpi];
  const rawValue =
    kpi === 'cpa'
      ? targets.cpa
      : kpi === 'conversions'
        ? targets.conversions
        : targets.roas;
  const [focused, setFocused] = useState(false);
  const showZero = meta.allowZero && !focused;
  const displayValue =
    Number.isFinite(rawValue) && (rawValue !== 0 || showZero) ? rawValue : '';

  const handleChange = (v: number) => {
    if (kpi === 'cpa') onChange({ cpa: v });
    else if (kpi === 'conversions') onChange({ conversions: v });
    else onChange({ roas: v });
  };

  return (
    <div className='space-y-1'>
      <label className='text-xs uppercase tracking-wide text-gray-500 font-semibold block'>
        {meta.label}
      </label>
      <div className={cn('relative', FIELD_WIDTH)}>
        {meta.prefix ? (
          <span className='absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500 pointer-events-none'>
            {meta.prefix}
          </span>
        ) : null}
        <Input
          type='number'
          step={meta.step}
          value={displayValue}
          onChange={(e) => {
            const parsed = Number(e.target.value);
            handleChange(Number.isFinite(parsed) ? parsed : 0);
          }}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          className={cn(FIELD_INPUT_CLASS, meta.prefix && 'pl-7')}
        />
      </div>
      <p className='text-[11px] text-gray-500'>
        {historicalValue === null
          ? 'No recent data'
          : `Last 90 days: ${formatKpiValue(kpi, historicalValue)}`}
      </p>
    </div>
  );
}

function AovInput({
  value,
  onChange,
  trackedAov,
}: {
  value: number;
  onChange: (v: number) => void;
  /** Average conversion value tracked in Google Ads; 0 when none is tracked. */
  trackedAov: number;
}) {
  const [focused, setFocused] = useState(false);
  const hasGoogleValue = trackedAov > 0;
  const hasOverride = Number.isFinite(value) && value > 0;
  // The field prefills with the Google value when the user hasn't entered
  // their own. While focused it shows whatever they're typing (blank until
  // they do), so the prefill never blocks an override.
  const displayValue = hasOverride
    ? round2(value)
    : focused
      ? ''
      : hasGoogleValue
        ? round2(trackedAov)
        : '';

  return (
    <div className='space-y-1'>
      <label className='text-xs uppercase tracking-wide text-gray-500 font-semibold block'>
        Average conversion value
      </label>
      <div className='flex items-center gap-2'>
        <div className={cn('relative', FIELD_WIDTH)}>
          <span className='absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500 pointer-events-none'>
            €
          </span>
          <Input
            type='number'
            step={1}
            value={displayValue}
            placeholder={hasGoogleValue ? round2(trackedAov).toString() : '0'}
            onChange={(e) => {
              const parsed = Number(e.target.value);
              onChange(Number.isFinite(parsed) ? parsed : 0);
            }}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            className={cn(FIELD_INPUT_CLASS, 'pl-7')}
          />
        </div>
        {hasGoogleValue ? (
          <span className='text-[11px] text-gray-500 whitespace-nowrap'>
            {formatKpiValue('aov', trackedAov)} from Google
          </span>
        ) : null}
      </div>
      {hasGoogleValue ? (
        <p className='text-[11px] text-gray-500'>
          Prefilled from Google Ads. Override it if your average order value differs.
        </p>
      ) : null}
    </div>
  );
}
